import React, { useState, useEffect } from 'react';
import { useAzureAuth } from '@/hooks/useAzureAuth';

export const DirectAuthFlow: React.FC = () => {
  const { login, loading, error } = useAzureAuth();
  const [usePopup, setUsePopup] = useState(false);

  useEffect(() => {
    // Check for bypass authentication token in URL hash
    const hash = window.location.hash;
    if (hash && hash.includes('access_token')) {
      const params = new URLSearchParams(hash.substring(1));
      const accessToken = params.get('access_token');
      
      if (accessToken) {
        // Store the token in localStorage for the app to use
        const tokenData = {
          accessToken,
          idToken: params.get('id_token') || '',
          refreshToken: '',
          expiresIn: parseInt(params.get('expires_in') || '3600'),
          tokenType: 'Bearer'
        };
        
        localStorage.setItem('bypass_auth_tokens', JSON.stringify(tokenData));
        
        // Clear the hash and redirect to dashboard
        history.replaceState(null, '', window.location.pathname);
        window.location.href = '/dashboard';
      }
    }
  }, []);

  const handleDirectLogin = async () => {
    try {
      await login(usePopup);
    } catch (err) {
      // Error is handled by the hook
    }
  };

  const testProtectedEndpoint = async () => {
    try {
      // Get API base URL from config service
      const { configService } = await import('../services/configService');
      const config = await configService.getConfig();
      
      const response = await fetch(`${config.api.baseUrl}/api/test-protected`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.status === 401) {
        alert(`✅ Test successful!\n\nExpected 401 Unauthorized response received.\nStatus: ${response.status}\nThis confirms the endpoint is properly protected.`);
      } else {
        const data = await response.json().catch(() => ({ error: 'Unknown response' }));
        alert(`❌ Unexpected response!\n\nStatus: ${response.status}\nResponse: ${JSON.stringify(data, null, 2)}\n\nExpected: 401 Unauthorized`);
      }
    } catch (error) {
      alert(`Network error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  return (
    <div className="auth-container">
      <div className="direct-auth-content">
        <div className="auth-header">
          <h1>Welcome</h1>
          <p>Sign in with your Azure AD account to continue.</p>
        </div>
        
        <div className="auth-benefits">
          <div className="benefit">
            <span className="benefit-icon">🔒</span>
            <span>Secure Azure AD authentication</span>
          </div>
          <div className="benefit">
            <span className="benefit-icon">⚡</span>
            <span>Instant access after login</span>
          </div>
          <div className="benefit">
            <span className="benefit-icon">🌐</span>
            <span>Single sign-on with Microsoft services</span>
          </div>
        </div>
        
        <div className="auth-action">
          <button 
            onClick={handleDirectLogin}
            disabled={loading}
            className="auth-button primary"
          >
            {loading ? (
              <>
                <div className="button-spinner"></div>
                Signing in...
              </>
            ) : (
              `Sign In with Azure AD ${usePopup ? '(Popup)' : '(Redirect)'}`
            )}
          </button>
          
          <div className="auth-options">
            <label className="checkbox-label">
              <input 
                type="checkbox" 
                checked={usePopup}
                onChange={(e) => setUsePopup(e.target.checked)}
                disabled={loading}
              />
              <span>Use popup window instead of redirect</span>
            </label>
            <div className="method-info">
              {usePopup ? (
                <span className="info-text">
                  ℹ️ Popup: Opens Azure AD in a new window. May be blocked by browsers.
                </span>
              ) : (
                <span className="info-text">
                  ℹ️ Redirect: Navigates to Azure AD in the same window (recommended).
                </span>
              )}
            </div>
          </div>
          
          {error && (
            <div className="error-message">
              <strong>Authentication failed:</strong> {error}
              <button onClick={handleDirectLogin} className="retry-button">
                Try Again
              </button>
            </div>
          )}
        </div>

        <div className="test-section">
          <button 
            onClick={testProtectedEndpoint}
            className="auth-button secondary"
          >
            Test Protected Endpoint (Should get 401)
          </button>
        </div>
        
        <div className="auth-info">
          <p>
            You'll be redirected to Microsoft's secure login page. 
            After authentication, you'll return here automatically.
          </p>
        </div>
      </div>
      
      <style>{`
        .auth-container {
          max-width: 500px;
          margin: 0 auto;
          padding: 2rem;
          background: white;
          border-radius: 12px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
          margin-top: 8vh;
        }

        .direct-auth-content {
          text-align: center;
        }

        .auth-header h1 {
          color: #333;
          margin-bottom: 0.5rem;
          font-size: 2rem;
          font-weight: 600;
        }

        .auth-header p {
          color: #666;
          margin-bottom: 2rem;
          font-size: 1.1rem;
        }

        .auth-benefits {
          margin-bottom: 2rem;
          text-align: left;
        }

        .benefit {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          margin-bottom: 0.75rem;
          padding: 0.5rem;
          background: #f8f9fa;
          border-radius: 6px;
        }

        .benefit-icon {
          font-size: 1.2rem;
        }

        .benefit span:last-child {
          color: #333;
          font-size: 14px;
        }

        .auth-action {
          margin-bottom: 1.5rem;
        }

        .auth-button.primary {
          background: linear-gradient(45deg, #0078d4, #106ebe);
          color: white;
          border: none;
          padding: 14px 28px;
          border-radius: 8px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          width: 100%;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          min-height: 50px;
        }

        .auth-button.primary:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(0, 120, 212, 0.3);
        }

        .auth-button.primary:disabled {
          background: #ccc;
          cursor: not-allowed;
          transform: none;
          box-shadow: none;
        }

        .auth-button.secondary {
          background: #6c757d;
          color: white;
          border: none;
          padding: 10px 20px;
          border-radius: 6px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          width: 100%;
          transition: all 0.2s ease;
        }

        .auth-button.secondary:hover {
          background: #5a6268;
          transform: translateY(-1px);
        }

        .test-section {
          margin: 1.5rem 0;
          padding: 1rem;
          background: #f8f9fa;
          border-radius: 8px;
          border: 1px solid #e9ecef;
        }

        .button-spinner {
          width: 16px;
          height: 16px;
          border: 2px solid transparent;
          border-top: 2px solid currentColor;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .error-message {
          margin-top: 1rem;
          padding: 1rem;
          background: #ffebee;
          border: 1px solid #f8bbd9;
          border-radius: 6px;
          color: #d32f2f;
          text-align: left;
        }

        .retry-button {
          background: #d32f2f;
          color: white;
          border: none;
          padding: 6px 12px;
          border-radius: 4px;
          font-size: 12px;
          cursor: pointer;
          margin-top: 0.5rem;
          float: right;
        }

        .retry-button:hover {
          background: #c62828;
        }

        .auth-options {
          margin-top: 1rem;
          padding: 1rem;
          background: #f8f9fa;
          border-radius: 8px;
          text-align: left;
        }

        .checkbox-label {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          cursor: pointer;
          font-size: 14px;
          color: #333;
        }

        .checkbox-label input[type="checkbox"] {
          cursor: pointer;
        }

        .method-info {
          margin-top: 0.75rem;
          padding: 0.75rem;
          background: white;
          border-radius: 6px;
          border: 1px solid #e0e0e0;
        }

        .info-text {
          font-size: 13px;
          color: #555;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .auth-info {
          padding-top: 1rem;
          border-top: 1px solid #eee;
        }

        .auth-info p {
          color: #666;
          font-size: 14px;
          margin: 0;
          line-height: 1.4;
        }
      `}</style>
    </div>
  );
};